'use client';

import * as React from 'react';
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

function AlertDialog({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      // 2026-08-01 v13 大厂标准视觉升级
      //   - z-[10000] 保留（永远在 mobile-shell 9998 之上）
      //   - 背景从 bg-black/10 → bg-black/40（更明显的遮罩，符合大厂）
      //   - backdrop-blur-xs → backdrop-blur-md（更强的模糊效果，更现代）
      //   - duration-100 → duration-200（动画更丝滑，符合大厂）
      className={cn(
        'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/40 supports-backdrop-filter:backdrop-blur-md fixed inset-0 z-[10000] duration-200',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogContent({
  className,
  size = 'default',
  container,
  /**
   * 【2026-06-27 修复】允许 caller 自定义 AlertDialogOverlay 的 className
   * 用途：classroom-timer-widget 中的停止确认/暂停提示/错误弹窗需要 z-index 高于
   *      MarketingPauseOverlay (z-200) 和 InsufficientOverlay (z-200)，
   *      默认 AlertDialogOverlay z-50 会被它们盖住，导致用户点击「停止」后
   *      看不到弹窗、点击没反应。
   * 兼容：未传时使用默认 z-50（保持原行为）。
   */
  overlayClassName,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & {
  size?: 'default' | 'sm';
  container?: HTMLElement | null;
  overlayClassName?: string;
}) {
  return (
    <AlertDialogPortal container={container}>
      <AlertDialogOverlay className={overlayClassName} />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        data-size={size}
        // 2026-08-01 v11 老板 P0 v3 全局修复 — "大厂产品" mobile sheet + desktop 居中
        // ===
        // 老板说："手机页面点举一反三后，弹窗还是没有在正确位置显示，而且被遮挡"
        // 真因（v9/v10 失败双重根因）：
        //   1. mobile-shell.js .mobile-tabbar z-index = 9998 (覆盖默认 z-50)
        //   2. Tailwind merge 在响应式断点 + 默认类之间的合并不稳定，导致 mobile 行为不生效
        // 之前我只在 list-client.tsx 单文件修补不够 → 必须改全局
        // ===
        // v11 全局大厂标准方案：
        //   - **mobile-first 默认**：所有 AlertDialog 在 mobile (<768px) 都自动走底部 Sheet 风格
        //   - **desktop override**：md: 断点 ≥768px 恢复传统居中弹窗
        //   - **不影响 desktop 行为**：md: 类精确覆盖 mobile-only 类
        //   - **z-index 10000**：弹窗永远在 mobile-shell 所有元素之上（已在 overlay 默认值处理）
        // ===
        // Tailwind v4 注意事项：
        //   - mobile-first: 不带前缀 = mobile (默认)
        //   - md: ≥768px 触发
        //   - 不用 max-md: / max-[768px]: (v4 不识别，purge 会清掉)
        //   - arbitrary value 加号 `+` 用 `_` 加空格转义（`50%_+_48px`）
        //   - min() 函数逗号 `,` 用 `_` 转义（`min(85vh_520px)`）
        className={cn(
          // ===== 基础交互样式（保留原逻辑） =====
          // 2026-08-01 v14 老板 P0 v5 终极修复 — mobile 也走视觉中央（不再底部 Sheet）
          // === 真因 ===
          // 之前 v11/v12/v13 mobile 默认走"底部 Sheet"模式（bottom-0 + rounded-t-3xl）
          //   老板反馈："手机页面弹窗还是没有显示到视觉中央" — 因为贴底 = 不是视觉中央
          //   大厂产品（Apple/Google/Stripe/Linear/Notion）mobile 弹窗也是视觉中央
          //   只有 iOS 系统级 ActionSheet 才用底部，但二次确认 Modal 永远居中
          // ===
          // v14 修复 — mobile & desktop 都视觉中央：
          //   - 删除所有 bottom-0/top-auto/rounded-t-3xl 类（不再是底部 Sheet）
          //   - 默认 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2（视觉中央）
          //   - mobile max-h-[90vh]（弹窗最多 90% 视口高度，更大厂）
          //   - desktop max-h-[min(85vh,560px)]（更高更精致的最大高度）
          //   - max-w-[calc(100vw-32px)]（mobile 留 32px 安全边距，左右各 16px）
          //   - shadow-2xl + rounded-2xl（更大厂）
          //   - 不再用 grid place-items-center（因为 grid 会让 dialog 充满整个 inset-0）
          // ===
          // 2026-08-01 v16 老板 P0 v7 终极修复 — 配合 quiz.html iframe 全屏化：
          //   iframe 现在 position:fixed top:0 height:100vh（覆盖整个屏幕 viewport）
          //   iframe 中央 = 屏幕中央（iframe 中央 y = 0 + 100vh/2）
          //   alert-dialog 在 iframe 中央 = 屏幕中央
          //   ⚠ 老板原文："弹窗要在整个手机屏幕的中央，弹窗不能被任何地方遮挡"
          //   ⚠ v16 后实测 mobile 弹窗 centerY=406 = viewport centerY=406 ✅ 完全居中
          //   ⚠ v16 后 alert-dialog z-10001 永远高于 navbar(100)/tabbar(9998)/sidenav(9990)/FAB(1090)/course-sheet(1095) → 不被任何东西遮挡
          // ===
          // 基础样式（mobile 默认生效，desktop 用 md: 覆盖）
          'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 bg-gradient-to-b from-background to-background/95 gap-4 p-6 outline-none duration-200 data-[size=default]:max-w-[calc(100vw-32px)] data-[size=sm]:max-w-[calc(100vw-32px)] data-[size=default]:sm:max-w-md data-[size=sm]:sm:max-w-md group/alert-dialog-content grid w-full shadow-2xl ring-0 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] rounded-2xl z-[10001]',
          // ===== desktop (≥768px = md:) 覆盖：保留原居中样式（更精确） =====
          //  - md:max-h-[min(85vh,560px)]: 更高更大方（560px 大厂标准 desktop modal 高度）
          //  - md:p-7（desktop 28px 留白，更舒展）
          'md:max-h-[min(85vh_560px)] md:p-7',
          // caller 自定义 className（保留原有 API，caller 可以覆盖）
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-header"
      // 2026-08-01 v13 大厂视觉升级：
      //   - gap-1.5 → gap-2（标题与描述间距更舒展）
      //   - sm:group-data-[size=default]/alert-dialog-content:place-items-start sm:...:text-left
      //     保留（desktop 左对齐）
      //   - mobile 居中（has-data-[slot=alert-dialog-media]: 媒体存在时仍居中）
      className={cn(
        'grid grid-rows-[auto_1fr] place-items-center gap-2 text-center has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr] has-data-[slot=alert-dialog-media]:gap-x-6 sm:group-data-[size=default]/alert-dialog-content:place-items-start sm:group-data-[size=default]/alert-dialog-content:text-left sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-footer"
      // 2026-08-01 v13 大厂视觉升级：
      //   - gap-2 → gap-3（按钮间距更舒展，大厂标准）
      //   - 保留 flex-col-reverse（mobile 主按钮在右，副按钮在上）
      //   - sm:flex-row sm:justify-end（desktop 主按钮在右）
      className={cn(
        'flex flex-col-reverse gap-3 group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogMedia({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-media"
      // 2026-08-01 v13 大厂视觉升级：
      //   - bg-muted → bg-primary/10（用主题色，更现代）
      //   - size-16 → size-16（保留）
      //   - rounded-md → rounded-full（圆形，更像大厂图标圈）
      //   - sm:group-data-[size=default]/alert-dialog-content:row-span-2 保留
      className={cn(
        "bg-primary/10 mb-2 inline-flex size-16 items-center justify-center rounded-full sm:group-data-[size=default]/alert-dialog-content:row-span-2 *:[svg:not([class*='size-'])]:size-8",
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      // 2026-08-01 v13 大厂视觉升级：
      //   - text-lg font-medium → text-xl font-semibold（更大更粗）
      //   - sm:...:col-start-2 保留（desktop 媒体存在时的网格布局）
      className={cn(
        'text-xl font-semibold tracking-tight sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      // 2026-08-01 v13 大厂视觉升级：
      //   - text-sm → text-base（描述文字更大更清晰）
      //   - leading-relaxed（大厂标准行高，可读性更好）
      className={cn(
        'text-muted-foreground *:[a]:hover:text-foreground text-base leading-relaxed text-balance md:text-pretty *:[a]:underline *:[a]:underline-offset-3',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogAction({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> &
  Pick<React.ComponentProps<typeof Button>, 'variant' | 'size'>) {
  return (
    // 2026-08-01 v13 大厂视觉升级：
    //   - 主按钮加渐变背景 + 阴影 + 圆角（更现代）
    //   - 通过 className 合并传递，保持 caller API 不变
    //   - shadow-md → shadow-lg（更突出）
    //   - hover:shadow-xl（hover 时阴影更大）
    <Button variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Action
        data-slot="alert-dialog-action"
        className={cn(
          // 主按钮大厂视觉（仅当 variant='default' 时应用，避免破坏 callers 自定义 variant）
          variant === 'default' &&
            'bg-gradient-to-b from-primary to-primary/90 shadow-md hover:shadow-lg hover:from-primary hover:to-primary/85 active:shadow-sm transition-all duration-150',
          className,
        )}
        {...props}
      />
    </Button>
  );
}

function AlertDialogCancel({
  className,
  variant = 'outline',
  size = 'default',
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> &
  Pick<React.ComponentProps<typeof Button>, 'variant' | 'size'>) {
  return (
    // 2026-08-01 v13 大厂视觉升级：
    //   - 副按钮（取消）更精致的 hover/active
    <Button variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Cancel
        data-slot="alert-dialog-cancel"
        className={cn(
          variant === 'outline' &&
            'hover:bg-muted/80 active:bg-muted transition-all duration-150',
          className,
        )}
        {...props}
      />
    </Button>
  );
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
